import { useEffect, useRef, useState } from "preact/hooks";
import type { ChatApi } from "../chat/api.ts";
import {
  firstQuestion,
  quizFailure,
  withAnswer,
  type Quiz,
  type QuizFailure,
  type QuizOptions,
  type QuizPosition,
  type QuizScope,
  type QuizScore,
} from "../chat/quiz.ts";
import { defineMessages, useT } from "../i18n/index.ts";
import { Icon } from "../icons/Icon.tsx";
import { inlineMarkdown } from "../contentLanguage.ts";
import { InlineText, LanguagePicker, LanguageQuestion, useDefaultLanguage } from "./ContentLanguage.tsx";

interface Props {
  api: ChatApi;
  productId: string;
  /** Où est le lecteur ; lu au moment de demander les portées et de créer un quiz. */
  position?: QuizPosition;
  /** Feuille du bas (mobile) : poignée affichée. */
  sheet: boolean;
  /** Retour à la discussion. */
  onBack(): void;
  /** Fermer le panneau. */
  onClose(): void;
}

const points = (n: number | string) => `${n} point${Number(n) > 1 ? "s" : ""}`;
const pointsEn = (n: number | string) => `${n} point${Number(n) === 1 ? "" : "s"}`;

const messages = defineMessages(
  {
    scopeCurrent: "En cours",
    scopePrevious: "Les chapitres précédents",
    failureUnavailable: "Le quiz n’est pas encore disponible pour ce livre.",
    failureDailyLimit: "Vous avez fait vos quiz du jour. Revenez demain !",
    failureTooShort: "Ce passage est trop court pour un quiz. Lisez encore un peu !",
    failureNotFound: "Votre position dans le livre n’a pas été retrouvée. Tournez une page puis réessayez.",
    failureOther: "Le quiz n’a pas pu être préparé. Réessayez dans un moment.",
    backToQuizzes: "Retour à mes quiz",
    backToChat: "Retour à la discussion",
    close: "Fermer",
    quiz: "Quiz",
    loading: "Chargement du quiz…",
    retry: "Réessayer",
    noQuizYet: "Pas encore de quiz terminé sur ce livre.",
    bookScoreLine: ({ points: n, percent }: { points: number; percent: number }) => `${points(n)} · Score du livre ${percent} %`,
    startQuiz: "Commencer un quiz",
    intro: "3 questions pour savoir si vous avez tout compris",
    ask: "Voulez-vous un quiz :",
    startCurrent: "Sur la lecture en cours",
    startPrevious: "Sur tout ce qui précède",
    resume: "Reprendre",
    firstChapter: "Vous êtes dans le premier chapitre : le quiz porte sur ce que vous venez de lire.",
    preparing: "Préparation du quiz…",
    rules: ({ first, second, bonus, max }: { first: number; second: number; bonus: number; max: number }) =>
      `Barème : ${points(first)} du premier coup, ${points(second)} au second essai. Bonus : +${bonus} si vous réussissez les 3 questions du premier coup. Maximum : ${points(max)}.`,
    leftToday: ({ n }: { n: number }) => `Encore ${n} quiz possible${n > 1 ? "s" : ""} aujourd’hui.`,
    myQuizzes: "Mes quiz",
    inProgress: "En cours",
    quizSummary: "Bilan du quiz",
    total: "Total : {points} / {max} points",
    flawless: ({ n }: { n: number }) => `Sans-faute ! +${points(n)} bonus`,
    noBonus: "Pas de bonus : il fallait 3 bonnes réponses du premier coup.",
    firstTry: "✓✓ du premier coup",
    secondTry: "✓ au second essai",
    missed: "✗ raté",
    bookScore: "Score du livre : {percent} %",
    questionOf: "Question {n} / {count}",
    wrongOneLeft: "Ce n’est pas ça. Il vous reste un essai.",
    bravo: "Bravo ! +{points} points",
    rightAnswer: "Bonne réponse ! +{points} point",
    missedAnswer: "Raté. La bonne réponse était : {answer}",
    inSection: "Dans « {section} »",
    seeSummary: "Voir le bilan",
    nextQuestion: "Question suivante",
    answerLater: "Je répondrai plus tard",
    cancelQuiz: "Annuler le quiz",
    cancelMaking: "Annuler",
    confirmCancel: "Annuler ce quiz ? Il disparaît de vos quiz et ne compte pas dans le score du livre, mais reste compté dans les quiz du jour.",
    confirmCancelYes: "Oui, annuler le quiz",
    deleteQuiz: "Supprimer ce quiz",
    confirmDelete: "Supprimer ce quiz ? Il ne comptera plus dans le score du livre.",
    confirmDeleteYes: "Supprimer",
    no: "Non",
  },
  {
    scopeCurrent: "Current section",
    scopePrevious: "Previous chapters",
    failureUnavailable: "The quiz isn’t available for this book yet.",
    failureDailyLimit: "You’ve done your quizzes for today. Come back tomorrow!",
    failureTooShort: "This passage is too short for a quiz. Read a little more!",
    failureNotFound: "Your position in the book could not be found. Turn a page and try again.",
    failureOther: "The quiz could not be prepared. Try again in a moment.",
    backToQuizzes: "Back to my quizzes",
    backToChat: "Back to the chat",
    close: "Close",
    quiz: "Quiz",
    loading: "Loading the quiz…",
    retry: "Try again",
    noQuizYet: "No quiz completed on this book yet.",
    bookScoreLine: ({ points: n, percent }) => `${pointsEn(n)} · Book score ${percent}%`,
    startQuiz: "Start a quiz",
    intro: "3 questions to check you’ve understood everything",
    ask: "Would you like a quiz:",
    startCurrent: "On what you’re reading now",
    startPrevious: "On everything before",
    resume: "Resume",
    firstChapter: "You’re in the first chapter: the quiz is about what you’ve just read.",
    preparing: "Preparing the quiz…",
    rules: ({ first, second, bonus, max }) =>
      `Scoring: ${pointsEn(first)} on the first try, ${pointsEn(second)} on the second. Bonus: +${bonus} if you get all 3 questions right on the first try. Maximum: ${pointsEn(max)}.`,
    leftToday: ({ n }) => `${n} more quiz${n === 1 ? "" : "zes"} available today.`,
    myQuizzes: "My quizzes",
    inProgress: "In progress",
    quizSummary: "Quiz results",
    total: "Total: {points} / {max} points",
    flawless: ({ n }) => `Perfect score! +${pointsEn(n)} bonus`,
    noBonus: "No bonus: it took 3 right answers on the first try.",
    firstTry: "✓✓ first try",
    secondTry: "✓ second try",
    missed: "✗ missed",
    bookScore: "Book score: {percent}%",
    questionOf: "Question {n} / {count}",
    wrongOneLeft: "That’s not it. You have one try left.",
    bravo: "Well done! +{points} points",
    rightAnswer: "Right answer! +{points} point",
    missedAnswer: "Missed. The right answer was: {answer}",
    inSection: "In “{section}”",
    seeSummary: "See the results",
    nextQuestion: "Next question",
    answerLater: "I’ll answer later",
    cancelQuiz: "Cancel the quiz",
    cancelMaking: "Cancel",
    confirmCancel: "Cancel this quiz? It disappears from your quizzes and doesn’t count in the book score, but still counts among today’s quizzes.",
    confirmCancelYes: "Yes, cancel the quiz",
    deleteQuiz: "Delete this quiz",
    confirmDelete: "Delete this quiz? It will no longer count in the book score.",
    confirmDeleteYes: "Delete",
    no: "No",
  },
);

const SCOPE_LABEL = { current: "scopeCurrent", previous: "scopePrevious" } as const satisfies Record<QuizScope, string>;
const START_LABEL = { current: "startCurrent", previous: "startPrevious" } as const satisfies Record<QuizScope, string>;
const FAILURE_TEXT = {
  unavailable: "failureUnavailable",
  daily_limit: "failureDailyLimit",
  too_short: "failureTooShort",
  not_found: "failureNotFound",
  other: "failureOther",
} as const satisfies Record<QuizFailure, string>;

type Screen =
  | { kind: "loading" }
  | { kind: "failed"; failure: QuizFailure }
  | { kind: "home"; options: QuizOptions; quizzes: Quiz[]; score: QuizScore; creating?: QuizScope; failure?: QuizFailure }
  | { kind: "playing"; quiz: Quiz; current: number; answering?: boolean; bookScore?: QuizScore }
  | { kind: "summary"; quiz: Quiz; bookScore?: QuizScore };

/**
 * Le quiz à la place de la discussion, repris de Studio : les portées proposées là où est le lecteur, le barème,
 * le score du livre et les quiz passés ; puis 3 questions à 3 choix avec deux essais ; puis le bilan.
 */
export function QuizPanel({ api, productId, position, sheet, onBack, onClose }: Props) {
  const t = useT(messages);
  const [screen, setScreen] = useState<Screen>({ kind: "loading" });
  // Où est le lecteur : tenu à jour, lu quand on demande les portées et quand on crée un quiz.
  const here = useRef(position);
  here.current = position ?? here.current;
  const body = useRef<HTMLDivElement>(null);
  // Le quiz est écrit dans la langue des drapeaux de son départ (celle choisie pour le livre tant qu'on ne la change pas
  // pour ce quiz), et la garde (Stéphane, 19/10/2026).
  const defaultLanguage = useDefaultLanguage();
  const [chosenLanguage, setChosenLanguage] = useState<string | null>(null);
  const language = useRef(defaultLanguage);
  language.current = chosenLanguage ?? defaultLanguage;

  const play = (quiz: Quiz) => setScreen({ kind: "playing", quiz, current: firstQuestion(quiz) });
  // Une question à confirmer (annuler le quiz, en supprimer un), posée dans le panneau.
  const [asking, setAsking] = useState<{ message: string; yes: string; then: () => void } | null>(null);
  // Un quiz en préparation annulé : supprimé dès qu'il arrive (sauf un quiz déjà commencé, repris, gardé tel quel).
  const making = useRef(0);

  const home = async (failure?: QuizFailure) => {
    setScreen({ kind: "loading" });
    const at = here.current;
    if (!at?.href) return; // Le livre s'ouvre encore : demandé une fois le lecteur placé.
    try {
      const [options, list] = await Promise.all([api.quizOptions(productId, at.href), api.bookQuizzes(productId)]);
      setScreen({ kind: "home", options, quizzes: list.quizzes, score: list.score, failure });
    } catch (error) {
      setScreen({ kind: "failed", failure: quizFailure(error) });
    }
  };
  const placed = !!position?.href;
  useEffect(() => {
    if (placed) void home();
  }, [api, productId, placed]);
  // Chaque écran commence en haut. Entre accolades : Chrome récent renvoie une promesse depuis scrollTo, qui ne doit
  // pas être prise pour le nettoyage de l'effet (« i is not a function » dans le Studio).
  useEffect(() => {
    body.current?.scrollTo?.({ top: 0 });
  }, [screen.kind, screen.kind === "playing" ? screen.current : 0]);

  const start = async (scope: QuizScope) => {
    const at = here.current;
    if (screen.kind !== "home" || screen.creating || !at) return;
    const shown = screen;
    setScreen({ ...shown, creating: scope, failure: undefined });
    const attempt = ++making.current;
    try {
      const quiz = await api.createQuiz(productId, scope, at, language.current);
      if (attempt === making.current) play(quiz);
      else if (!shown.quizzes.some((item) => item.id === quiz.id)) await api.deleteQuiz(quiz.id).catch(() => undefined);
    } catch (error) {
      if (attempt === making.current) setScreen({ ...shown, creating: undefined, failure: quizFailure(error) });
    }
  };
  const stopMaking = () => {
    making.current++;
    setScreen((shown) => (shown.kind === "home" ? { ...shown, creating: undefined } : shown));
  };

  // Pendant ses deux premières questions, le quiz peut attendre (il reste « En cours ») ; il s'annule à toute question
  // (Stéphane, 19/10/2026). Annulé ou supprimé, il n'apparaît plus et ne compte plus.
  const remove = (quizId: string, cancelling: boolean) =>
    setAsking({
      message: t(cancelling ? "confirmCancel" : "confirmDelete"),
      yes: t(cancelling ? "confirmCancelYes" : "confirmDeleteYes"),
      then: async () => {
        try {
          await api.deleteQuiz(quizId);
        } catch {
          return;
        }
        void home();
      },
    });

  const open = async (quizId: string) => {
    if (screen.kind !== "home") return;
    const shown = screen;
    try {
      play(await api.getQuiz(quizId));
    } catch (error) {
      setScreen({ ...shown, failure: quizFailure(error) });
    }
  };

  const answer = async (choice: number) => {
    if (screen.kind !== "playing" || screen.answering) return;
    const question = screen.quiz.questions?.[screen.current];
    if (!question || question.resolved || question.tries.includes(choice)) return;
    const shown = screen;
    setScreen({ ...shown, answering: true });
    try {
      const outcome = await api.answerQuiz(shown.quiz.id, question.index, choice);
      setScreen({ ...shown, answering: false, quiz: withAnswer(shown.quiz, outcome), bookScore: outcome.book_score ?? shown.bookScore });
    } catch {
      setScreen({ ...shown, answering: false });
    }
  };

  const next = () => {
    if (screen.kind !== "playing") return;
    const count = screen.quiz.questions?.length ?? 0;
    if (screen.current + 1 < count) setScreen({ ...screen, current: screen.current + 1 });
    else setScreen({ kind: "summary", quiz: screen.quiz, bookScore: screen.bookScore });
  };

  const inQuiz = screen.kind === "playing" || screen.kind === "summary";
  const backLabel = inQuiz ? t("backToQuizzes") : t("backToChat");
  const back = () => (inQuiz ? void home() : onBack());

  return (
    <section class="msb-chat msb-quiz" aria-label={t("quiz")}>
      {sheet && <div class="msb-sheet-handle" aria-hidden="true" />}
      <header class="msb-chat__header">
        <button type="button" class="msb-icon-button" onClick={onClose} aria-label={t("close")}>
          <Icon name="close" />
        </button>
        <h2>{t("quiz")}</h2>
        <div class="msb-chat__end">
          <button type="button" class="msb-icon-button" onClick={back} aria-label={backLabel} title={backLabel}>
            <Icon name="back" />
          </button>
        </div>
      </header>
      {/* Demandé au premier chat ou quiz sur un livre dans une autre langue (Stéphane, 19/10/2026). */}
      <LanguageQuestion />
      <div ref={body} class="msb-quiz__body" aria-live="polite">
        {asking && (
          <div class="msb-quiz__card msb-quiz__confirm" role="alertdialog" aria-label={asking.message}>
            <p>{asking.message}</p>
            <div class="msb-quiz__leave">
              <button type="button" class="msb-quiz__choice" onClick={() => setAsking(null)}>
                {t("no")}
              </button>
              <button
                type="button"
                class="msb-quiz__choice"
                data-danger="true"
                onClick={() => {
                  setAsking(null);
                  asking.then();
                }}
              >
                {asking.yes}
              </button>
            </div>
          </div>
        )}
        {screen.kind === "loading" && (
          <p class="msb-chat__thinking" role="status">
            <span class="msb-dots" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            {t("loading")}
          </p>
        )}

        {screen.kind === "failed" && (
          <div class="msb-quiz__card">
            <p role="alert">{t(FAILURE_TEXT[screen.failure])}</p>
            {screen.failure !== "unavailable" && (
              <button type="button" class="msb-quiz__choice" data-main="true" onClick={() => void home()}>
                {t("retry")}
              </button>
            )}
          </div>
        )}

        {screen.kind === "home" && (
          <>
            <p class="msb-quiz__score">
              {screen.score.percent === null
                ? t("noQuizYet")
                : t("bookScoreLine", { points: screen.score.points, percent: screen.score.percent })}
            </p>
            <div class="msb-quiz__card" role="group" aria-label={t("startQuiz")}>
              <p class="msb-quiz__question">{t("intro")}</p>
              <p class="msb-quiz__ask">{t("ask")}</p>
              {language.current && (
                <div class="msb-quiz__language">
                  <LanguagePicker value={language.current} onChange={setChosenLanguage} disabled={!!screen.creating} />
                </div>
              )}
              {screen.options.scopes.map((scope) => {
                const begun = !!screen.options.begun?.[scope];
                const section = here.current?.section || screen.options.section_label;
                return (
                  <button
                    key={scope}
                    type="button"
                    class="msb-quiz__choice"
                    data-main={scope === "current" ? "true" : undefined}
                    disabled={!!screen.creating || (screen.options.left_today === 0 && !begun)}
                    onClick={() => void start(scope)}
                  >
                    <span>{t(START_LABEL[scope])}</span>
                    {(begun || (scope === "current" && section)) && (
                      <small>{[begun ? t("resume") : "", scope === "current" ? section : ""].filter(Boolean).join(" · ")}</small>
                    )}
                  </button>
                );
              })}
              {screen.options.scopes.length === 1 && <p class="msb-quiz__note">{t("firstChapter")}</p>}
              {screen.creating && (
                <p class="msb-chat__thinking" role="status">
                  <span class="msb-dots" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </span>
                  {t("preparing")}
                </p>
              )}
              {screen.creating && (
                <button type="button" class="msb-quiz__choice msb-quiz__cancel" onClick={stopMaking}>
                  {t("cancelMaking")}
                </button>
              )}
              {screen.failure && (
                <p role="alert" class="msb-quiz__error">
                  {t(FAILURE_TEXT[screen.failure])}
                </p>
              )}
              {screen.options.left_today === 0 && !screen.failure && <p class="msb-quiz__error">{t(FAILURE_TEXT.daily_limit)}</p>}
            </div>
            <p class="msb-quiz__note">
              {t("rules", {
                first: screen.options.rules.first_try,
                second: screen.options.rules.second_try,
                bonus: screen.options.rules.bonus,
                max: screen.options.rules.max_points,
              })}
            </p>
            {screen.options.left_today !== null && screen.options.left_today > 0 && (
              <p class="msb-quiz__note">{t("leftToday", { n: screen.options.left_today })}</p>
            )}
            {screen.quizzes.length > 0 && (
              <div role="group" aria-label={t("myQuizzes")} class="msb-quiz__past">
                <h3>{t("myQuizzes")}</h3>
                {screen.quizzes.map((quiz) => (
                  <div key={quiz.id} class="msb-quiz__past-row">
                    <button type="button" class="msb-quiz__past-item" onClick={() => void open(quiz.id)}>
                      <span>
                        {t(SCOPE_LABEL[quiz.scope])}
                        {quiz.section_label && <small>{quiz.section_label}</small>}
                      </span>
                      <strong>{quiz.status === "completed" ? `${quiz.total ?? quiz.points} / ${quiz.max_points}` : t("inProgress")}</strong>
                    </button>
                    <button type="button" class="msb-icon-button msb-quiz__delete" aria-label={t("deleteQuiz")} title={t("deleteQuiz")} onClick={() => remove(quiz.id, false)}>
                      <Icon name="delete" size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {screen.kind === "playing" && <Question screen={screen} onAnswer={answer} onNext={next} />}
        {screen.kind === "playing" && screen.quiz.status !== "completed" && (
          <div class="msb-quiz__leave">
            {screen.current < 2 && (
              <button type="button" class="msb-quiz__choice" onClick={() => void home()}>
                {t("answerLater")}
              </button>
            )}
            <button type="button" class="msb-quiz__choice" data-danger="true" onClick={() => remove(screen.quiz.id, true)}>
              {t("cancelQuiz")}
            </button>
          </div>
        )}

        {screen.kind === "summary" && (
          <div class="msb-quiz__card" role="group" aria-label={t("quizSummary")}>
            <p class="msb-quiz__question">{t("total", { points: screen.quiz.total ?? screen.quiz.points, max: screen.quiz.max_points })}</p>
            <p class="msb-quiz__note">{screen.quiz.bonus > 0 ? t("flawless", { n: screen.quiz.bonus }) : t("noBonus")}</p>
            <ol class="msb-quiz__results">
              {(screen.quiz.questions ?? []).map((question) => (
                <li key={question.index}>
                  <span>
                    <InlineText text={question.question} />
                  </span>
                  <small>{question.first_try ? t("firstTry") : question.correct ? t("secondTry") : t("missed")}</small>
                </li>
              ))}
            </ol>
            {screen.bookScore?.percent != null && <p class="msb-quiz__score">{t("bookScore", { percent: screen.bookScore.percent })}</p>}
            <button type="button" class="msb-quiz__choice" data-main="true" onClick={() => void home()}>
              {t("myQuizzes")}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function Question({
  screen,
  onAnswer,
  onNext,
}: {
  screen: Extract<Screen, { kind: "playing" }>;
  onAnswer(choice: number): void;
  onNext(): void;
}) {
  const t = useT(messages);
  const questions = screen.quiz.questions ?? [];
  const question = questions[screen.current];
  if (!question) return null;
  const last = screen.current + 1 >= questions.length;
  const progress = t("questionOf", { n: screen.current + 1, count: questions.length });
  const state = (choice: number) =>
    question.resolved && choice === question.answer ? "right" : question.tries.includes(choice) ? "wrong" : undefined;
  return (
    <div class="msb-quiz__card" role="group" aria-label={progress}>
      <p class="msb-quiz__progress">{progress}</p>
      <p class="msb-quiz__question"><InlineText text={question.question} /></p>
      {question.choices.map((choice, index) => (
        <button
          key={index}
          type="button"
          class="msb-quiz__choice"
          data-state={state(index)}
          aria-pressed={question.tries.includes(index)}
          disabled={screen.answering || question.resolved || question.tries.includes(index)}
          onClick={() => onAnswer(index)}
        >
          <InlineText text={choice} />
        </button>
      ))}
      {!question.resolved && question.tries.length > 0 && (
        <p class="msb-quiz__feedback" data-state="wrong" role="status">
          {t("wrongOneLeft")}
        </p>
      )}
      {question.resolved && (
        <>
          <p class="msb-quiz__feedback" data-state={question.correct ? "right" : "wrong"} role="status">
            {question.first_try
              ? t("bravo", { points: question.points })
              : question.correct
                ? t("rightAnswer", { points: question.points })
                : t("missedAnswer", { answer: inlineMarkdown(question.choices[question.answer ?? 0] ?? "").map((part) => part.text).join("") })}
          </p>
          {question.explanation && <p class="msb-quiz__explanation"><InlineText text={question.explanation} /></p>}
          {question.section && <p class="msb-quiz__note">{t("inSection", { section: question.section })}</p>}
          <button type="button" class="msb-quiz__choice" data-main="true" onClick={onNext}>
            {last ? t("seeSummary") : t("nextQuestion")}
          </button>
        </>
      )}
    </div>
  );
}
